import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {snackbarAtom} from 'atoms/snackbar';
import type {
  Employee,
  EmployeeLdap,
  EmployeeRoleLevel,
  WorkstreamBasic,
  WorkstreamEmployee,
} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {usePlanningId} from 'components/DSYS/CollabProvider';
import {markdownTable} from 'components/DSYS/editor/dynamicNode/markdownExport';
import {mergeEditorStateMapIntoObjectFieldWithSource} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentField} from 'components/DSYS/editor/dynamicNode/PlanningCommentField';
import {planningMapKeys} from 'components/DSYS/editor/dynamicNode/planningCommentMapKeys';
import {getColumnWidthsFromValue} from 'components/DSYS/editor/dynamicNode/planningTable/columnWidthPersistence';
import {useColumnResizePersistence} from 'components/DSYS/editor/dynamicNode/planningTable/useColumnResizePersistence';
import {
  PLANNING_TABLE_SHELL_STYLE,
  PlanningTableWideShell,
} from 'components/DSYS/editor/dynamicNode/planningTable/WideTableShell';
import type {
  DynamicNodeDefinition,
  DynamicNodeRenderProps,
} from 'components/DSYS/editor/dynamicNode/types';
import {
  type MergePersistedMapKeyArgs,
  useDynamicNodeEditorStateMap,
} from 'components/DSYS/editor/dynamicNode/useDynamicNodeEditorStateMap';
import {resolveSwitchboardTableEmbedRootId} from 'components/DSYS/editor/dynamicNode/workstreamEmbedRootResolution';
import {getTypeaheadNodeTableContext} from 'components/DSYS/editor/nodes/TypeaheadNode';
import typeaheadStyles from 'components/DSYS/editor/nodes/TypeaheadNode.module.css';
import {TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND} from 'components/DSYS/editor/plugins/SelectedTextToolbar';
import {Link} from 'components/DSYS/Link';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {type ReactNode, useCallback, useMemo} from 'react';
import {useLocation} from 'react-router';
import {getFrontendUrl} from 'utilities';
import {useEditorWorkstreams, useIsPlanningDocPublished, usePlanning} from 'views/planning/hooks';
import {
  replaceRoleAssociations,
  toWorkstreamEditPayload,
} from 'views/workstreams/detail/workstreamEditPayload';
import {useWorkstream, useWorkstreamEdit} from 'views/workstreams/hooks';
import {buildSidebarOriginState} from 'views/workstreams/routeUtils';
import {EmployeeSelect, MultiEmployeeSelect} from 'views/workstreams/TODODSYSStuff';
import {
  getWorkstreamDri,
  getWorkstreamExecSponsor,
  getWorkstreamWorkingGroup,
} from 'views/workstreams/utils';

export const WORKING_TEAM_KIND = 'working_team';

export type WorkingTeamBinding = {
  kind: typeof WORKING_TEAM_KIND;
};

export type WorkingTeamValue = {
  dri?: EmployeeLdap;
  execSponsor?: EmployeeLdap;
  programManager?: EmployeeLdap;
  otherMembers?: EmployeeLdap[];
  /** JSON string: Record<role row key, serialized Lexical> for planning comments on role labels. */
  editorStateMap?: string;
  /** Persisted column widths keyed by `Column.type` (px), same pattern as workstream table embed. */
  columnWidths?: Record<string, number>;
};

type WorkingTeamRowKind =
  | 'dri'
  | 'exec_sponsor'
  | 'program_manager'
  | 'other_members'
  | 'workstream_link';

type WorkingTeamRow = {
  kind: WorkingTeamRowKind;
};

const WORKING_TEAM_ROWS: WorkingTeamRow[] = [
  {kind: 'dri'},
  {kind: 'exec_sponsor'},
  {kind: 'program_manager'},
  {kind: 'other_members'},
  {kind: 'workstream_link'},
];

const WORKING_TEAM_TABLE_COLUMNS_BASE: Column<WorkingTeamRow>[] = [
  {
    type: 'working_team_role',
    minWidth: 200,
    width: 300,
    cantDisable: true,
  },
  {
    type: 'working_team_names',
    minWidth: 200,
    width: 445,
    spanner: true,
    cantDisable: true,
  },
];

const WORKING_TEAM_COLUMN_LABEL_OVERRIDES = {
  working_team_role: 'group_role',
  working_team_names: 'working_team_names_column',
} as const;

function parseWorkingTeamValue(raw: unknown): WorkingTeamValue {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const o = raw as Record<string, unknown>;
  const other = o.otherMembers;
  return {
    dri: o.dri as EmployeeLdap | undefined,
    execSponsor: o.execSponsor as EmployeeLdap | undefined,
    programManager: o.programManager as EmployeeLdap | undefined,
    otherMembers: Array.isArray(other)
      ? (other.filter(
          (x): x is EmployeeLdap =>
            !!x &&
            typeof x === 'object' &&
            typeof (x as EmployeeLdap).user_id === 'string' &&
            typeof (x as EmployeeLdap).ldap === 'string'
        ) as EmployeeLdap[])
      : undefined,
    editorStateMap: typeof o.editorStateMap === 'string' ? o.editorStateMap : undefined,
    columnWidths: (() => {
      const cw = getColumnWidthsFromValue(raw);
      return Object.keys(cw).length > 0 ? cw : undefined;
    })(),
  };
}

function mergeValue(prev: WorkingTeamValue, patch: Partial<WorkingTeamValue>): WorkingTeamValue {
  return {...prev, ...patch};
}

/** Matches `replaceRoleAssociations` but keeps full `employee` objects for React Query cache patches. */
function buildOptimisticEmployeeAssociations(
  current: WorkstreamEmployee[] | undefined,
  roleId: string,
  employees: Employee[]
): WorkstreamEmployee[] {
  const next = (current ?? []).filter((association) => association.dri !== roleId);
  const seen = new Set<string>();
  for (const employee of employees) {
    if (seen.has(employee.user_id)) {
      continue;
    }
    seen.add(employee.user_id);
    next.push({
      dri: roleId,
      employee: employee as EmployeeRoleLevel,
    });
  }
  return next;
}

function employeeRoleToLdap(employee: EmployeeRoleLevel | undefined): EmployeeLdap | undefined {
  if (!employee) {
    return undefined;
  }
  return {
    user_id: employee.user_id,
    ldap: employee.ldap,
    name: employee.name,
    email: employee.email,
    is_deactivated: employee.is_deactivated,
  };
}

/**
 * Workstream `employee_associations` use `reporter` for the progress reporter role;
 * we surface that as the program manager default in planning docs.
 */
function getWorkstreamProgramManagerDefault(
  workstream: WorkstreamBasic | undefined
): EmployeeRoleLevel | undefined {
  return workstream?.employee_associations?.find(({dri}) => dri === 'reporter')?.employee;
}

function buildWorkingTeamDefaultsFromWorkstream(
  workstream: WorkstreamBasic | undefined
): WorkingTeamValue | null {
  if (!workstream) {
    return null;
  }
  const otherMembers = getWorkstreamWorkingGroup(workstream)
    .map(employeeRoleToLdap)
    .filter((e): e is EmployeeLdap => !!e);
  return {
    dri: employeeRoleToLdap(getWorkstreamDri(workstream)),
    execSponsor: employeeRoleToLdap(getWorkstreamExecSponsor(workstream)),
    programManager: employeeRoleToLdap(getWorkstreamProgramManagerDefault(workstream)),
    otherMembers: otherMembers.length > 0 ? otherMembers : undefined,
  };
}

function workingTeamRowRoleLabel(kind: WorkingTeamRowKind): string {
  switch (kind) {
    case 'dri':
      return t('dri');
    case 'exec_sponsor':
      return t('exec_sponsor');
    case 'program_manager':
      return t('working_team_program_manager');
    case 'other_members':
      return t('working_team_other_members');
    case 'workstream_link':
      return t('working_team_workstream_link');
  }
}

const ReadOnlyPerson = ({employee}: {employee: EmployeeLdap}) => (
  <Box display="flex" alignItems="center" style={{gap: 6}}>
    <Avatar user={employee} size="xsmall" />
    <Text size="small">{employee.name}</Text>
  </Box>
);

type PlanningWorkstreamLinkTarget = {
  id: number;
  name: string;
};

const DocumentWorkstreamLinks = ({
  planningId,
  narrativeWorkstreamsLoading,
  narrativeTargets,
  switchboardId,
  switchboardWorkstreamLoading,
  rootId,
  rootWorkstream,
  rootWorkstreamLoading,
}: {
  planningId?: string;
  narrativeWorkstreamsLoading: boolean;
  narrativeTargets: PlanningWorkstreamLinkTarget[];
  switchboardId: number | undefined;
  switchboardWorkstreamLoading: boolean;
  rootId: number | undefined;
  rootWorkstream: {id: number; name: string} | undefined;
  rootWorkstreamLoading: boolean;
}) => {
  const location = useLocation();
  const sidebarOriginState = buildSidebarOriginState({
    search: location.search,
    state: location.state,
  });
  const linkState = {source: 'dynamic_working_team', ...sidebarOriginState};

  if (!planningId) {
    return (
      <Text size="small" color="subtle">
        {t('dynamic_node_workstream_table_no_planning')}
      </Text>
    );
  }

  if (narrativeWorkstreamsLoading) {
    return (
      <Text size="small" color="subtle">
        {t('working_team_links_loading')}
      </Text>
    );
  }

  if (narrativeTargets.length > 0) {
    return (
      <Stack gap="6">
        {narrativeTargets.map((target) => (
          <Text key={target.id}>
            <Link
              to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(target.id))}
              state={linkState}
              openInNewTab
              hasNoUnderline
              showUnderlineOnHover
            >
              {getFrontendUrl().replace(/\/+$/, '')}
              {ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(target.id))}
            </Link>
          </Text>
        ))}
      </Stack>
    );
  }

  if (switchboardId !== undefined && switchboardWorkstreamLoading) {
    return (
      <Text size="small" color="subtle">
        {t('working_team_links_loading')}
      </Text>
    );
  }

  if (rootId !== undefined && rootWorkstreamLoading && !rootWorkstream) {
    return (
      <Text size="small" color="subtle">
        {t('working_team_links_loading')}
      </Text>
    );
  }

  if (rootWorkstream) {
    return (
      <Text>
        <Link
          to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(rootWorkstream.id))}
          state={linkState}
          openInNewTab
          hasNoUnderline
          showUnderlineOnHover
        >
          {getFrontendUrl().replace(/\/+$/, '')}
          {ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(rootWorkstream.id))}
        </Link>
      </Text>
    );
  }

  return (
    <Text size="small" color="subtle">
      {t('dynamic_node_workstream_table_no_narrative')}
    </Text>
  );
};

/** Workstream `employee_associations` role id for the program manager row. */
const WORKSTREAM_PROGRAM_MANAGER_ROLE = 'reporter';

const WorkingTeam = ({nodeKey, editor, value, setValue}: DynamicNodeRenderProps) => {
  const editable = editor.isEditable();
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const {planning} = usePlanning(planningId ?? '');
  const {narrativeWorkstreams, isLoading: narrativeWorkstreamsLoading} = useEditorWorkstreams(
    planningId ?? ''
  );

  const switchboardId = planning?.switchboard_id ?? undefined;
  const {workstream: switchboardWorkstream, isLoading: switchboardWorkstreamLoading} =
    useWorkstream(switchboardId);

  const accessibleNarrative = useMemo(
    () => narrativeWorkstreams.filter((w) => w.is_accessible),
    [narrativeWorkstreams]
  );

  const isNarrativeFlatMode = accessibleNarrative.length >= 1;

  const narrativeTargets = useMemo(
    (): PlanningWorkstreamLinkTarget[] =>
      accessibleNarrative.map((w) => ({
        id: w.workstream_id,
        name: w.workstream.name,
      })),
    [accessibleNarrative]
  );

  const tableContext = getTypeaheadNodeTableContext(editor, nodeKey);
  const {bodyRowCount, rowIndex, workstreamNameNodeCount} = tableContext;

  const switchboardSubWorkstreamIds = useMemo(
    () => switchboardWorkstream?.dependencies.map((dependency) => dependency.dependent.id) ?? [],
    [switchboardWorkstream?.dependencies]
  );

  const tableContextSlice = useMemo(
    () => ({bodyRowCount, rowIndex, workstreamNameNodeCount}),
    [bodyRowCount, rowIndex, workstreamNameNodeCount]
  );

  const resolvedFromPlanning = useMemo(() => {
    if (accessibleNarrative.length >= 1) {
      return undefined;
    }
    if (switchboardId && switchboardWorkstream) {
      return resolveSwitchboardTableEmbedRootId(
        switchboardId,
        switchboardSubWorkstreamIds,
        tableContextSlice
      );
    }
    return undefined;
  }, [
    accessibleNarrative.length,
    switchboardId,
    switchboardSubWorkstreamIds,
    switchboardWorkstream,
    tableContextSlice,
  ]);

  const rootId = isNarrativeFlatMode ? undefined : resolvedFromPlanning;

  const seedWorkstreamId = useMemo(() => {
    if (narrativeTargets.length > 0) {
      return narrativeTargets[0].id;
    }
    return rootId;
  }, [narrativeTargets, rootId]);

  const {workstream: seedWorkstream, isLoading: seedWorkstreamLoading} =
    useWorkstream(seedWorkstreamId);

  const workstreamBacked = seedWorkstreamId !== undefined;
  const nodeValue = useMemo(() => parseWorkingTeamValue(value), [value]);

  const {
    columns: workingTeamTableColumns,
    tableWidthPx: workingTeamTableWidthPx,
    onColumnResizeEnd: workingTeamColumnResizeEnd,
  } = useColumnResizePersistence({
    editor,
    nodeKey,
    setValue,
    baseColumns: WORKING_TEAM_TABLE_COLUMNS_BASE,
    value,
    enabled: true,
    allowResize: editable,
  });

  const teamView = useMemo((): WorkingTeamValue => {
    if (seedWorkstream) {
      return buildWorkingTeamDefaultsFromWorkstream(seedWorkstream) ?? {};
    }
    if (workstreamBacked && seedWorkstreamLoading) {
      return {};
    }
    return nodeValue;
  }, [nodeValue, seedWorkstream, seedWorkstreamLoading, workstreamBacked]);

  const {editWorkstream} = useWorkstreamEdit();

  const persistEmployeesForRole = useCallback(
    (roleId: string, employees: Employee[]) => {
      if (!seedWorkstream) {
        return;
      }
      const payload = toWorkstreamEditPayload(seedWorkstream);
      const nextAssociations = replaceRoleAssociations(
        payload.employee_associations ?? [],
        roleId,
        employees
      );
      const optimisticEmployeeAssociations = buildOptimisticEmployeeAssociations(
        seedWorkstream.employee_associations,
        roleId,
        employees
      );
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
      void editWorkstream({
        reason: 'Update working team',
        data: {...payload, employee_associations: nextAssociations},
        optimisticEmployeeAssociations,
      })
        .catch(() => {
          setSnackbarMessage({text: t('error_occurred_please_try_again')});
        })
        .finally(() => {
          editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
        });
    },
    [editWorkstream, editor, seedWorkstream, setSnackbarMessage]
  );

  const isWorkstreamRestricted = seedWorkstream?.name === 'Restricted';
  const useApiForRoles =
    Boolean(seedWorkstream) && editable && !isPlanningDocPublished && !isWorkstreamRestricted;

  const canEditNodeFallback =
    editable &&
    !isPlanningDocPublished &&
    (!workstreamBacked || (!seedWorkstreamLoading && !seedWorkstream));

  const patchNode = useCallback(
    (next: Partial<WorkingTeamValue>) => {
      setValue(mergeValue(nodeValue, next));
    },
    [nodeValue, setValue]
  );

  const mergePersistedMapKey = useCallback(
    ({rawNodeValue, mapKey, serializedState, sourceText}: MergePersistedMapKeyArgs) =>
      mergeEditorStateMapIntoObjectFieldWithSource(
        parseWorkingTeamValue(rawNodeValue) as WorkingTeamValue & Record<string, unknown>,
        'editorStateMap',
        mapKey,
        serializedState,
        sourceText
      ),
    []
  );

  const {persistKey} = useDynamicNodeEditorStateMap<WorkingTeamValue>({
    editor,
    nodeKey,
    setValue: (next) => setValue(next),
    mergePersistedMapKey,
  });

  const columnHeaderLabel = useMemo((): Record<string, ReactNode> | undefined => {
    if (!planningId) {
      return undefined;
    }
    const mapJson = nodeValue.editorStateMap;
    return {
      working_team_role: (
        <PlanningCommentField
          mapKey={planningMapKeys.workingTeam.table.columnHeader('working_team_role')}
          mapJson={mapJson}
          fallbackText={t('group_role')}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      working_team_names: (
        <PlanningCommentField
          mapKey={planningMapKeys.workingTeam.table.columnHeader('working_team_names')}
          mapJson={mapJson}
          fallbackText={t('working_team_names_column')}
          planningId={planningId}
          onPersistKey={persistKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
    };
  }, [nodeValue.editorStateMap, persistKey, planningId]);

  const renderRoleLabelCell = (kind: WorkingTeamRowKind) => {
    const defaultLabel = workingTeamRowRoleLabel(kind);
    return (
      <PlanningCommentField
        mapKey={planningMapKeys.workingTeam.roleLabel(kind)}
        mapJson={nodeValue.editorStateMap}
        fallbackText={defaultLabel}
        planningId={planningId}
        onPersistKey={persistKey}
      />
    );
  };

  const otherMembers = teamView.otherMembers ?? [];

  const hasPeople =
    !!teamView.dri ||
    !!teamView.execSponsor ||
    !!teamView.programManager ||
    otherMembers.length > 0;

  const roleRowLoading = workstreamBacked && seedWorkstreamLoading && !seedWorkstream;

  const renderNamesCell = (kind: WorkingTeamRowKind) => {
    switch (kind) {
      case 'dri': {
        if (roleRowLoading) {
          return (
            <Text size="small" color="subtle">
              {t('working_team_links_loading')}
            </Text>
          );
        }
        const dri = teamView.dri;
        if (useApiForRoles) {
          return (
            <EmployeeSelect
              selection={dri}
              setSelection={(e?: Employee) => {
                if (e) {
                  void persistEmployeesForRole('dri', [e]);
                }
              }}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (canEditNodeFallback) {
          return (
            <EmployeeSelect
              selection={dri}
              setSelection={(e?: Employee) => patchNode({dri: e as EmployeeLdap | undefined})}
              disabled={false}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (dri) {
          return <ReadOnlyPerson employee={dri} />;
        }
        return (
          <Text size="small" color="subtle">
            —
          </Text>
        );
      }
      case 'exec_sponsor': {
        if (roleRowLoading) {
          return (
            <Text size="small" color="subtle">
              {t('working_team_links_loading')}
            </Text>
          );
        }
        const execSponsor = teamView.execSponsor;
        if (useApiForRoles) {
          return (
            <EmployeeSelect
              selection={execSponsor}
              setSelection={(e?: Employee) => {
                void persistEmployeesForRole('exec_sponsor', e ? [e] : []);
              }}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (canEditNodeFallback) {
          return (
            <EmployeeSelect
              selection={execSponsor}
              setSelection={(e?: Employee) =>
                patchNode({execSponsor: e as EmployeeLdap | undefined})
              }
              disabled={false}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (execSponsor) {
          return <ReadOnlyPerson employee={execSponsor} />;
        }
        return (
          <Text size="small" color="subtle">
            —
          </Text>
        );
      }
      case 'program_manager': {
        if (roleRowLoading) {
          return (
            <Text size="small" color="subtle">
              {t('working_team_links_loading')}
            </Text>
          );
        }
        const programManager = teamView.programManager;
        if (useApiForRoles) {
          return (
            <EmployeeSelect
              selection={programManager}
              setSelection={(e?: Employee) => {
                void persistEmployeesForRole(WORKSTREAM_PROGRAM_MANAGER_ROLE, e ? [e] : []);
              }}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
              allowEmptySelection
            />
          );
        }
        if (canEditNodeFallback) {
          return (
            <EmployeeSelect
              selection={programManager}
              setSelection={(e?: Employee) =>
                patchNode({programManager: e as EmployeeLdap | undefined})
              }
              disabled={false}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
              allowEmptySelection
            />
          );
        }
        if (programManager) {
          return <ReadOnlyPerson employee={programManager} />;
        }
        return (
          <Text size="small" color="subtle">
            —
          </Text>
        );
      }
      case 'other_members': {
        if (roleRowLoading) {
          return (
            <Text size="small" color="subtle">
              {t('working_team_links_loading')}
            </Text>
          );
        }
        if (useApiForRoles) {
          return (
            <MultiEmployeeSelect
              selections={otherMembers}
              setSelections={(employees) => {
                void persistEmployeesForRole('working_group', employees);
              }}
              autoFocus={false}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (canEditNodeFallback) {
          return (
            <MultiEmployeeSelect
              selections={otherMembers}
              setSelections={(employees) => patchNode({otherMembers: employees as EmployeeLdap[]})}
              disabled={false}
              autoFocus={false}
              inputClassName={typeaheadStyles.employeeTypeaheadInput}
            />
          );
        }
        if (otherMembers.length > 0) {
          return (
            <Stack gap="6">
              {otherMembers.map((m) => (
                <ReadOnlyPerson key={m.user_id} employee={m} />
              ))}
            </Stack>
          );
        }
        return (
          <Text size="small" color="subtle">
            —
          </Text>
        );
      }
      case 'workstream_link':
        return (
          <DocumentWorkstreamLinks
            planningId={planningId}
            narrativeWorkstreamsLoading={narrativeWorkstreamsLoading}
            narrativeTargets={narrativeTargets}
            switchboardId={switchboardId}
            switchboardWorkstreamLoading={switchboardWorkstreamLoading}
            rootId={rootId}
            rootWorkstream={seedWorkstream}
            rootWorkstreamLoading={seedWorkstreamLoading}
          />
        );
      default:
        return null;
    }
  };

  if (!editable) {
    if (!hasPeople && !planningId) {
      return null;
    }
  }

  return (
    <Box width="100%" marginY="8">
      <PlanningTableWideShell editor={editor} tableWidthPx={workingTeamTableWidthPx}>
        <Table
          columns={workingTeamTableColumns}
          data={WORKING_TEAM_ROWS}
          headerTextSize="medium"
          columnLabelOverrides={WORKING_TEAM_COLUMN_LABEL_OVERRIDES}
          columnHeaderLabel={columnHeaderLabel}
          lastColumnResizeHandle
          onColumnResizeEnd={workingTeamColumnResizeEnd}
          style={PLANNING_TABLE_SHELL_STYLE}
        >
          <Table.Header />
          <Table.Body>
            {WORKING_TEAM_ROWS.map((row) => (
              <Table.Row key={row.kind}>
                <Table.Cell>
                  <Box>{renderRoleLabelCell(row.kind)}</Box>
                </Table.Cell>
                <Table.Cell>
                  <Box>{renderNamesCell(row.kind)}</Box>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </PlanningTableWideShell>
    </Box>
  );
};

function exportWorkingTeamAsMarkdownTable(value: unknown): string {
  const v = parseWorkingTeamValue(value);
  const namesColumn = (raw: string) => raw.trim() || '-';
  const others = (v.otherMembers ?? [])
    .map((e) => e.name?.trim())
    .filter(Boolean)
    .join(', ');
  return markdownTable(
    [t('group_role'), t('working_team_names_column')],
    [
      [workingTeamRowRoleLabel('dri'), namesColumn(v.dri?.name ?? '')],
      [workingTeamRowRoleLabel('exec_sponsor'), namesColumn(v.execSponsor?.name ?? '')],
      [workingTeamRowRoleLabel('program_manager'), namesColumn(v.programManager?.name ?? '')],
      [workingTeamRowRoleLabel('other_members'), namesColumn(others)],
      [workingTeamRowRoleLabel('workstream_link'), '-'],
    ]
  );
}

export const workingTeamDefinition: DynamicNodeDefinition = {
  kindId: WORKING_TEAM_KIND,
  labelKey: 'dynamic_node_working_team',
  categoryKey: 'dynamic_node_category_document',
  defaultBinding: {kind: WORKING_TEAM_KIND} satisfies WorkingTeamBinding,
  Component: WorkingTeam,
  exportHtml: ({value}) => exportWorkingTeamAsMarkdownTable(value),
};
